import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap, tap } from 'rxjs';
import { SOCKET_ROOM } from 'src/app/core/enums/events-buffer/socket/socket-rooms-enum';
import { SOCKET_TICKET_ENTRY } from 'src/app/core/enums/events-buffer/socket/socket-ticket-event.enum';
import { Logger } from 'src/app/core/interfaces/Logger';
import { SocketRoomHandlerService } from 'src/app/core/services/socket/socket-room-handler.service';
import { SocketService } from 'src/app/core/services/socket/socket.service';
import { TicketEventsService } from '../../application/services/events.service';
import { RegisterTicketSerieService } from '../../application/services/register-ticket-serie.service';
import { ITicketSeriePayload } from '../../interfaces/ticket-serie-info.interface';
import { Ticket } from '../../domain/model/ticket.model';
import { GetLastTicketUseCase } from '../../application/usecases/get-last-ticket.usecase';

@Injectable({ providedIn: 'root' })
export class TicketUpdatedListener {
  private readonly logger = inject(Logger);
  private readonly socketService = inject(SocketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly registerTicketSerieService = inject(RegisterTicketSerieService);
  private readonly socketRoomHandlerService = inject(SocketRoomHandlerService);
  private readonly ticketEventsService = inject(TicketEventsService);
  private readonly getLastTicketUseCase = inject(GetLastTicketUseCase);

  constructor() {
    this.socketRoomHandlerService
      .listenToRoom(SOCKET_ROOM.ticket)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.initializeTicketListeners();
      });
  }

  private initializeTicketListeners(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.ticket} 📄] [Ticket serie updated listener initialized]`
    );

    this.socketService
      .receive<ITicketSeriePayload>(SOCKET_TICKET_ENTRY.sendTicketSerie)
      .pipe(
        switchMap((eventResponse: ITicketSeriePayload) =>
          this.getLastTicketUseCase.execute(eventResponse.payload.ticket_id).pipe(
            filter((ticket: Ticket | null): ticket is Ticket => ticket !== null),
            switchMap(() =>
              this.registerTicketSerieService.execute(eventResponse.payload)
            )
          )
        ),
        tap((ticket: Ticket | null) => {
          if (ticket) {
            this.ticketEventsService.emitAccountInvoiceReceived(ticket);
            this.ticketEventsService.emitTicketUpdated(ticket);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
